import React, { useState, useEffect } from 'react';
import { Star, Send, CheckCircle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SubmitReview = () => {
  const [formData, setFormData] = useState({
    author: '',
    location: '',
    rating: 5,
    text: '',
    dsgvo_consent: false
  });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({
      ...prev,
      rating
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.author.trim() || !formData.text.trim()) {
      toast({
        title: "Fehler",
        description: "Bitte fuellen Sie alle Pflichtfelder aus",
        variant: "destructive"
      });
      return;
    }

    if (!formData.dsgvo_consent) {
      toast({
        title: "Zustimmung erforderlich",
        description: "Bitte stimmen Sie der Datenschutzerklärung zu, um Ihre Bewertung zu senden.",
        variant: "destructive"
      });
      return;
    }

    setSubmitting(true);

    try {
      await axios.post(`${BACKEND_URL}/api/testimonials/submit`, formData);
      setSubmitted(true);
      toast({
        title: "Vielen Dank!",
        description: "Ihre Bewertung wurde eingereicht und wird nach Pruefung veroeffentlicht."
      });
    } catch (error) {
      console.error('Error submitting review:', error);
      toast({
        title: "Fehler",
        description: "Bewertung konnte nicht gesendet werden. Bitte versuchen Sie es erneut.",
        variant: "destructive"
      });
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = () => {
    return (
      <div className="flex gap-2">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => handleRatingClick(star)}
            className="focus:outline-none transition-transform hover:scale-110"
          >
            <Star
              size={32}
              className={`${
                star <= formData.rating 
                  ? 'text-yellow-400 fill-yellow-400' 
                  : 'text-gray-300 hover:text-yellow-300'
              } transition-colors`}
            />
          </button>
        ))}
      </div>
    );
  };

  if (submitted) {
    return (
      <div className="pt-20">
        <section className="section-spacing-large">
          <div className="photo-container">
            <div className="max-w-2xl mx-auto text-center">
              <div className="w-24 h-24 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-8">
                <CheckCircle size={48} className="text-green-600" />
              </div>
              <h1 className="hero-title mb-6">Vielen Dank!</h1>
              <p className="subtitle mb-8">
                Ihre Bewertung wurde erfolgreich eingereicht und wird nach Pruefung veroeffentlicht.
              </p>
              <a href="/" className="btn-primary">
                Zurueck zur Startseite
              </a>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="section-spacing-large bg-gray-50">
        <div className="photo-container text-center">
          <h1 className="hero-title mb-6">Bewertung abgeben</h1>
          <p className="subtitle max-w-2xl mx-auto">
            Teilen Sie Ihre Erfahrung mit anderen Kunden
          </p>
        </div>
      </section>

      {/* Form Section */}
      <section className="section-spacing">
        <div className="photo-container">
          <div className="max-w-2xl mx-auto">
            <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-lg p-8">
              {/* Name */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ihr Name *
                </label>
                <input
                  type="text"
                  name="author"
                  value={formData.author}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                  placeholder="z.B. Maria Mueller"
                  required
                />
              </div>

              {/* Location */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ihr Wohnort (optional)
                </label>
                <input
                  type="text"
                  name="location"
                  value={formData.location}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors"
                  placeholder="z.B. Berlin"
                />
              </div>

              {/* Rating */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Ihre Bewertung *
                </label>
                {renderStars()}
                <p className="text-sm text-gray-500 mt-2">
                  Klicken Sie auf die Sterne, um Ihre Bewertung zu waehlen
                </p>
              </div>

              {/* Review Text */}
              <div className="mb-8">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ihre Bewertung *
                </label>
                <textarea
                  name="text"
                  value={formData.text}
                  onChange={handleChange}
                  rows="5"
                  className="w-full px-4 py-3 border-2 border-gray-200 rounded-lg focus:border-primary focus:outline-none transition-colors resize-none"
                  placeholder="Erzaehlen Sie von Ihrer Erfahrung mit meiner Fotografie..."
                  required
                />
              </div>

              {/* DSGVO Consent */}
              <div className="mb-6 flex items-start gap-3 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <input
                  type="checkbox"
                  id="review_dsgvo_consent"
                  name="dsgvo_consent"
                  checked={formData.dsgvo_consent}
                  onChange={handleChange}
                  data-testid="review-dsgvo-consent"
                  className="mt-1 h-5 w-5 flex-shrink-0 rounded border-gray-300 text-primary focus:ring-primary cursor-pointer"
                />
                <label htmlFor="review_dsgvo_consent" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
                  Ich habe die <a href="/datenschutz" target="_blank" rel="noopener noreferrer" className="text-primary underline hover:no-underline">Datenschutzerklärung</a> gelesen und stimme der Speicherung und Veröffentlichung meiner Bewertung auf dieser Website zu. Meine Einwilligung kann ich jederzeit für die Zukunft per E-Mail widerrufen. *
                </label>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                data-testid="review-submit-btn"
                className="w-full btn-primary flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {submitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Wird gesendet...
                  </>
                ) : (
                  <>
                    <Send size={20} />
                    Bewertung absenden
                  </>
                )}
              </button>

              <p className="text-xs text-gray-500 mt-4 text-center">
                Ihre Bewertung wird nach Pruefung veroeffentlicht.
              </p>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
};

export default SubmitReview;
